import { CopyIcon, DeleteIcon } from '@chakra-ui/icons';
import {
    Alert,
    AlertIcon,
    Box, Button, FormControl, FormLabel, Heading,
    HStack,
    IconButton,
    Input, NumberInput,
    NumberInputField, Select, Table, Tbody,
    Td,
    Tr,
    useClipboard, useToast,
    VStack
} from '@chakra-ui/react';
import { useState } from 'react';

export default function PriceCalculator() {
    const [cptList, setCptList] = useState([{ code: '', qty: 1 }]);
    const [markup, setMarkup] = useState(3);
    const [docusignPrice, setDocusignPrice] = useState('');
    const toast = useToast();
    const { onCopy, setValue } = useClipboard('');
    const [billingType, setBillingType] = useState('Test');

    const dummyPrices = {
        '83726': 25,
        '83711': 18,
        '42126': 30
    };

    const handleChange = (index, field, value) => {
        const updated = [...cptList];
        updated[index][field] = field === 'qty' ? Number(value) : value;
        setCptList(updated);
    };

    const handleAdd = () => {
        setCptList([...cptList, { code: '', qty: 1 }]);
    };

    const handleRemove = (index) => {
        const updated = [...cptList];
        updated.splice(index, 1);
        setCptList(updated);
    };

    const getResults = () => {
        const validCPTs = cptList.filter(item => item.code.trim());
        const totalQty = validCPTs.reduce((sum, item) => sum + item.qty, 0);
        const sharedCash = totalQty > 0 ? (Number(docusignPrice) / totalQty).toFixed(2) : '0.00';

        return validCPTs.map(item => {
            const price = dummyPrices[item.code] || 0;
            const total = price * item.qty * markup;

            return {
                code: item.code,
                qty: item.qty,
                insurance: total.toFixed(2),
                medical: total.toFixed(2),
                medicare: total.toFixed(2),
                hmo: total.toFixed(2),
                cash: sharedCash // same for all
            };
        });
    };


    const renderRow = (label, value) => (
        <Tr>
            <Td>{label}</Td>
            <Td>${value}</Td>
            <Td>
                <IconButton
                    icon={<CopyIcon />} size="sm"
                    onClick={() => { setValue(value); onCopy(); toast({ title: 'Copied' }); }}
                />
            </Td>
        </Tr>
    );

    const results = getResults();

    return (
        <Box maxW="700px" mx="auto" mt={10} p={4}>
            <Heading size="md" mb={6}>Multi CPT Price Calculator</Heading>
            <VStack spacing={4} align="stretch">

                <FormControl>
                    <FormLabel>Billing Type</FormLabel>
                    <Select value={billingType} onChange={(e) => setBillingType(e.target.value)}>
                        <option value="Test">Test</option>
                        <option value="Panel">Panel</option>
                    </Select>
                </FormControl>


                {cptList.map((item, i) => (
                    <HStack key={i}>
                        <FormControl>
                            <FormLabel>CPT Code</FormLabel>
                            <Input
                                value={item.code}
                                onChange={(e) => handleChange(i, 'code', e.target.value)}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Qty</FormLabel>
                            <NumberInput value={item.qty} min={1} onChange={(v) => handleChange(i, 'qty', v)}>
                                <NumberInputField />
                            </NumberInput>
                        </FormControl>
                        <IconButton icon={<DeleteIcon />} colorScheme="red" onClick={() => handleRemove(i)} />
                    </HStack>
                ))}
                <Button onClick={handleAdd}>Add CPT Code</Button>

                <FormControl>
                    <FormLabel>Markup</FormLabel>
                    <NumberInput value={markup} min={1} onChange={(v) => setMarkup(Number(v))}>
                        <NumberInputField />
                    </NumberInput>
                </FormControl>
                <FormControl>
                    <FormLabel>Docusign Price</FormLabel>
                    <NumberInput value={docusignPrice} min={0} onChange={(v) => setDocusignPrice(v)}>
                        <NumberInputField />
                    </NumberInput>
                </FormControl>
                {billingType === 'Panel' && cptList.filter(i => i.code.trim()).length === 1 && (
                    <Alert status="warning">
                        <AlertIcon />
                        Go to panels and check Bundle Panel
                    </Alert>
                )}

                {results.map((r, idx) => (
                    <Box key={idx} p={4} borderWidth="1px" borderRadius="md">
                        <Heading size="sm" mb={2}>CPT {r.code} × {r.qty}</Heading>
                        <Table variant="simple" size="sm">
                            <Tbody>
                                {renderRow('Insurance', r.insurance)}
                                {renderRow('Medical', r.medical)}
                                {renderRow('Medicare', r.medicare)}
                                {renderRow('HMO', r.hmo)}
                                {renderRow('Cash/Patient', r.cash)}
                                {renderRow('Client', r.cash)}
                            </Tbody>
                        </Table>
                    </Box>
                ))}
            </VStack>
        </Box>
    );
}
